/**
 * useD4RTData Hook
 *
 * Custom React hook for D4RT 4D video analysis API calls.
 * Handles loading states, error handling, and response caching.
 *
 * Routes through Express backend proxy at /api/d4rt → Python D4RT service (localhost:8001)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { mapToGCSUri, isValidGCSUri } from '../utils/gcsUriMapper';

// D4RT service URL — routes through Express backend proxy at /api/d4rt
// Express proxies to the D4RT Python service (localhost:8001 by default)
const D4RT_SERVICE_URL = process.env.REACT_APP_D4RT_SERVICE_URL || '/api/d4rt';

/**
 * D4RT API Error class with Axios-style structure.
 * Provides status code, response data, and request config for proper error handling.
 */
class D4RTError extends Error {
  constructor(message, { status = null, statusText = '', data = null, config = {} } = {}) {
    super(message);
    this.name = 'D4RTError';
    this.isD4RTError = true;
    // Axios-style response structure
    this.response = status ? { status, statusText, data } : null;
    this.config = config;
    this.code = status || 'ERR_NETWORK';
  }
}

/**
 * Upload a blob URL to the D4RT service via /upload endpoint.
 * Fetches the blob, wraps it in FormData, and POSTs to the service.
 * Optionally sends user_id and session_id for GCS path organization and BigQuery metadata.
 *
 * @param {string} blobUrl - blob: or data: URL from the browser
 * @param {string} serviceUrl - D4RT service base URL
 * @param {Object} [options] - Optional upload parameters
 * @param {string} [options.userId] - User ID for GCS path organization
 * @param {string} [options.sessionId] - Session ID for GCS path organization
 * @returns {Promise<Object>} Upload result with video_id, upload_id, gcs_uri
 */
async function uploadBlobToService(blobUrl, serviceUrl, { userId, sessionId } = {}) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  const formData = new FormData();
  formData.append('video', blob, 'uploaded_video.mp4');

  if (userId) {
    formData.append('user_id', userId);
  }
  if (sessionId) {
    formData.append('session_id', sessionId);
  }

  const uploadRes = await fetch(`${serviceUrl}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!uploadRes.ok) {
    let errData = null;
    try { const cloned = uploadRes.clone(); const text = await cloned.text(); errData = JSON.parse(text); } catch { /* ignore parse errors */ }
    throw new D4RTError(
      `Video upload failed (${uploadRes.status})`,
      {
        status: uploadRes.status,
        statusText: uploadRes.statusText,
        data: errData,
        config: { url: `${serviceUrl}/upload`, method: 'POST' },
      }
    );
  }
  const data = await uploadRes.json();
  // Backend returns upload_id as video_id, plus gcs_uri for long-term reference
  return {
    videoId: data.video_id,
    uploadId: data.upload_id,
    gcsUri: data.gcs_uri || null,
    userId: data.user_id || null,
    sessionId: data.session_id || null,
  };
}

/**
 * useD4RTData Hook
 *
 * @returns {Object} API methods and state
 */
export const useD4RTData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);

  // Cache for responses (prevent duplicate requests)
  const cacheRef = useRef(new Map());

  // Last upload metadata (GCS URI, session, etc.)
  const lastUploadRef = useRef(null);
  const uploadCacheRef = useRef(new Map()); // original video URL -> upload metadata

  // Abort controller for request cancellation
  const abortControllerRef = useRef(null);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    uploadCacheRef.current.clear();
    lastUploadRef.current = null;
  }, []);

  /**
   * Cancel pending requests
   */
  const cancelRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Generic API call wrapper
   */
  const callAPI = useCallback(async (endpoint, data, options = {}) => {
    const { useCache = true, timeout = 30000 } = options;

    // Handle video_id: blob URLs get uploaded, file paths get GCS-mapped
    // Work on a shallow copy to avoid mutating the caller's object
    const payload = data ? { ...data } : data;
    if (payload && payload.video_id) {
      if (payload.video_id.startsWith('blob:') || payload.video_id.startsWith('data:')) {
        const originalVideoId = payload.video_id;
        const cachedUpload = uploadCacheRef.current.get(originalVideoId);

        if (cachedUpload) {
          payload.video_id = cachedUpload.videoId;
          lastUploadRef.current = cachedUpload;
          console.log('[useD4RTData] Reusing cached upload_id for blob video:', cachedUpload.uploadId);
        } else {
          // Upload blob once, then reuse upload_id for all subsequent D4RT calls.
          console.log('[useD4RTData] Detected blob URL, uploading to D4RT service...');
          const uploadResult = await uploadBlobToService(
            originalVideoId,
            D4RT_SERVICE_URL,
            { userId: payload._userId, sessionId: payload._sessionId },
          );

          const cachedMeta = {
            videoId: uploadResult.videoId,
            uploadId: uploadResult.uploadId,
            gcsUri: uploadResult.gcsUri,
            userId: uploadResult.userId,
            sessionId: uploadResult.sessionId,
          };

          uploadCacheRef.current.set(originalVideoId, cachedMeta);
          payload.video_id = cachedMeta.videoId;
          lastUploadRef.current = cachedMeta;

          console.log('[useD4RTData] Uploaded, server video_id:', payload.video_id, 'gcs_uri:', uploadResult.gcsUri);
        }

        // Clean up internal-only fields before sending to API
        delete payload._userId;
        delete payload._sessionId;
      } else if (!isValidGCSUri(payload.video_id)) {
        // Try GCS mapping for file paths (seg_NNNN.mp4 patterns)
        const mappedUri = mapToGCSUri(payload.video_id);
        if (mappedUri) {
          console.log(`[useD4RTData] Mapped to GCS URI: ${mappedUri}`);
          payload.video_id = mappedUri;
        }
        // If mapToGCSUri returns null, pass video_id through as-is (local path)
      }
    }

    // Generate cache key
    const cacheKey = `${endpoint}:${JSON.stringify(payload)}`;

    // Check cache
    if (useCache && cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setLastResponse(cached);
      return cached;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const timeoutId = setTimeout(() => {
        abortControllerRef.current?.abort();
      }, timeout);

      const response = await fetch(`${D4RT_SERVICE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errData = null;
        try { const text = await response.text(); errData = JSON.parse(text); } catch { /* ignore */ }
        throw new D4RTError(
          `HTTP ${response.status}: ${response.statusText}`,
          {
            status: response.status,
            statusText: response.statusText,
            data: errData,
            config: { url: `${D4RT_SERVICE_URL}${endpoint}`, method: 'POST', payload },
          }
        );
      }

      const result = await response.json();

      // Cache successful response
      if (useCache) {
        cacheRef.current.set(cacheKey, result);
      }

      setLastResponse(result);
      setIsLoading(false);
      return result;

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[useD4RTData] Request cancelled');
        setError(null);
      } else {
        console.error('[useD4RTData] API error:', err);
        setError(err.message);
      }
      setIsLoading(false);
      throw err;
    }
  }, []);

  /**
   * Track a 2D point across frames
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {Object} point - {u: number, v: number}
   * @param {number} startFrame - Start frame index
   * @param {number} endFrame - End frame index
   * @param {string} trackId - Optional track identifier
   * @returns {Promise<Object>} Trajectory data
   */
  const trackPoint = useCallback(async (videoId, point, startFrame, endFrame, trackId = null) => {
    return callAPI('/track_point', {
      video_id: videoId,
      point,
      start_frame: startFrame,
      end_frame: endFrame,
      track_id: trackId,
    });
  }, [callAPI]);

  /**
   * Get depth estimation at a point
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {Object} point - {u: number, v: number}
   * @param {number} frameIndex - Frame index
   * @returns {Promise<Object>} Depth data
   */
  const getDepth = useCallback(async (videoId, point, frameIndex) => {
    return callAPI('/get_depth', {
      video_id: videoId,
      point,
      frame_index: frameIndex,
    });
  }, [callAPI]);

  /**
   * Render novel view
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {number} sourceFrame - Source frame index
   * @param {number} targetFrame - Target frame index
   * @param {Object} queryPoint - {u: number, v: number}
   * @returns {Promise<Object>} Rendered view data
   */
  const renderView = useCallback(async (videoId, sourceFrame, targetFrame, queryPoint) => {
    return callAPI('/render_view', {
      video_id: videoId,
      source_frame: sourceFrame,
      target_frame: targetFrame,
      query_point: queryPoint,
    }, { useCache: false }); // Don't cache rendered views
  }, [callAPI]);

  /**
   * Baseball-specific analysis
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {string} analysisType - 'release_point' | 'arm_slot' | 'tipping_detection' | 'recruiting_eval'
   * @param {string} pitcherName - Optional pitcher name
   * @param {Array} frameRange - Optional [start, end] frame range
   * @returns {Promise<Object>} Analysis results
   */
  const analyzeBaseball = useCallback(async (videoId, analysisType, pitcherName = null, frameRange = null) => {
    return callAPI('/analyze/baseball', {
      video_id: videoId,
      analysis_type: analysisType,
      pitcher_name: pitcherName,
      frame_range: frameRange,
    });
  }, [callAPI]);

  /**
   * Get skeletal tracking across frames
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {number} startFrame - Start frame index
   * @param {number} endFrame - End frame index
   * @param {Array<string>} jointNames - Optional joint names to track
   * @returns {Promise<Object>} Skeletal tracking data
   */
  const getSkeletalTrack = useCallback(async (videoId, startFrame, endFrame, jointNames = null) => {
    return callAPI('/get_skeletal_track', {
      video_id: videoId,
      start_frame: startFrame,
      end_frame: endFrame,
      joint_names: jointNames,
    });
  }, [callAPI]);

  /**
   * Get action/object trajectory (sport-agnostic)
   *
   * @param {string} videoId - Video ID or GCS URI
   * @param {number} startFrame - Start frame
   * @param {number} endFrame - End frame
   * @param {Object} initialPoint - {u: number, v: number} starting point
   * @returns {Promise<Object>} Action trajectory data with sport detection
   */
  const getActionTrajectory = useCallback(async (videoId, startFrame, endFrame, initialPoint) => {
    return callAPI('/get_action_trajectory', {
      video_id: videoId,
      start_frame: startFrame,
      end_frame: endFrame,
      initial_point: initialPoint,
    }, { useCache: false }); // Don't cache trajectories (always fresh)
  }, [callAPI]);

  // Backward compatibility alias
  const getPitchTrajectory = getActionTrajectory;

  /**
   * Check service health
   *
   * @returns {Promise<Object>} Health status
   */
  // Throttle health checks — only check once per 60s to avoid console spam
  const lastHealthCheckRef = useRef(0);
  const lastHealthResultRef = useRef(null);

  const checkHealth = useCallback(async () => {
    const now = Date.now();
    // Return cached result if checked within last 60 seconds
    if (lastHealthResultRef.current && now - lastHealthCheckRef.current < 60000) {
      return lastHealthResultRef.current;
    }
    try {
      const response = await fetch(`${D4RT_SERVICE_URL}/health`);
      if (!response.ok) {
        throw new Error('Service unhealthy');
      }
      const result = await response.json();
      lastHealthCheckRef.current = now;
      lastHealthResultRef.current = result;
      return result;
    } catch {
      const result = { status: 'unhealthy', error: 'Service offline', code: 'D4RT_SERVICE_DOWN' };
      lastHealthCheckRef.current = now;
      lastHealthResultRef.current = result;
      return result;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelRequests();
    };
  }, [cancelRequests]);

  return {
    // API methods
    trackPoint,
    getDepth,
    renderView,
    analyzeBaseball,
    getSkeletalTrack,
    getActionTrajectory,
    getPitchTrajectory, // Backward compatibility
    checkHealth,

    // Utility methods
    clearCache,
    cancelRequests,

    // State
    isLoading,
    error,
    lastResponse,
    lastUpload: lastUploadRef.current,

    // Service info
    serviceUrl: D4RT_SERVICE_URL,
  };
};

export default useD4RTData;
